import blurWGSL from '../../shaders/blur.wgsl';
import compositeWGSL from '../../shaders/composite.wgsl';
import cfg from '../../config.json';

const WG = 16; // workgroup tile size (16×16 = 256 threads)
const MAX_FACES = 64;

export class WebGPUBlurrer {
  static async create() {
    if (!navigator.gpu) throw new Error('WebGPU is not available');
    const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
    if (!adapter) throw new Error('No suitable WebGPU adapter found');
    const device = await adapter.requestDevice();
    return new WebGPUBlurrer(device);
  }

  constructor(device) {
    this.device = device;
    this._gpuOk = true;

    // Surface GPU validation errors in the console
    device.addEventListener('uncapturederror', e => {
      console.error('[WebGPU] uncaptured error:', e.error.message ?? e.error);
      this._gpuOk = false;
    });
    device.lost.then(info => {
      console.error('[WebGPU] device lost:', info.message);
      this._gpuOk = false;
    });

    this._buildPipelines();
  }

  _buildPipelines() {
    const d = this.device;
    this._blurPipeline = d.createComputePipeline({
      layout: 'auto',
      compute: {
        module: d.createShaderModule({ code: blurWGSL }),
        entryPoint: 'main',
      },
    });
    this._compositePipeline = d.createComputePipeline({
      layout: 'auto',
      compute: {
        module: d.createShaderModule({ code: compositeWGSL }),
        entryPoint: 'main',
      },
    });
  }

  /**
   * Apply Gaussian blur or pixelation to face regions.
   *
   * @param {ImageData} imageData
   * @param {Array<{x1,y1,x2,y2}>} detections
   * @param {object}  opts
   * @param {number}  opts.radius      blur kernel radius (pixels)
   * @param {number}  opts.sigma       Gaussian sigma
   * @param {boolean} opts.pixelate    pixelate instead of blur
   * @param {number}  opts.pixelSize   block size for pixelation
   * @returns {Promise<ImageData>}
   */
  async blurFaces(imageData, detections, opts = {}) {
    // If a previous GPU call failed, skip straight to the canvas fallback
    if (!this._gpuOk) return this._canvasFallback(imageData, detections, opts);

    try {
      const result = await this._blurFacesGPU(imageData, detections, opts);

      // Sanity-check: if the GPU wrote all-zero pixels on a non-black frame,
      // something went wrong (shader error, unsupported texture format, etc.)
      const srcHasContent = imageData.data[0] || imageData.data[1] || imageData.data[2];
      if (srcHasContent && !result.data[0] && !result.data[1] && !result.data[2]) {
        console.warn('[WebGPU] output is all-black — switching to Canvas fallback');
        this._gpuOk = false;
        return this._canvasFallback(imageData, detections, opts);
      }

      return result;
    } catch (err) {
      console.error('[WebGPU] blurFaces error, switching to Canvas fallback:', err);
      this._gpuOk = false;
      return this._canvasFallback(imageData, detections, opts);
    }
  }

  async _canvasFallback(imageData, detections, opts) {
    if (!this._canvas) {
      const { CanvasBlurrer } = await import('./canvas.js');
      this._canvas = new CanvasBlurrer();
    }
    return this._canvas.blurFaces(imageData, detections, opts);
  }

  async _blurFacesGPU(imageData, detections, {
    radius = 21,
    sigma = 15,
    pixelate = false,
    pixelSize = 16,
  } = {}) {
    const { width, height, data } = imageData;
    const d = this.device;

    // --- Textures -------------------------------------------------------
    // src:     CPU→GPU upload target + shader read
    // temp:    intermediate after H-blur
    // blurred: after V-blur
    // out:     composite result, read back to CPU

    const baseUsage = GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.STORAGE_BINDING;
    const srcTex  = d.createTexture({ size: [width, height], format: 'rgba8unorm',
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST });
    const tmpTex  = d.createTexture({ size: [width, height], format: 'rgba8unorm', usage: baseUsage });
    const blrTex  = d.createTexture({ size: [width, height], format: 'rgba8unorm', usage: baseUsage });
    const outTex  = d.createTexture({ size: [width, height], format: 'rgba8unorm',
      usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.COPY_SRC });

    // Upload frame
    d.queue.writeTexture(
      { texture: srcTex },
      data,
      { bytesPerRow: width * 4 },
      [width, height],
    );

    // --- Encode commands ------------------------------------------------
    const enc = d.createCommandEncoder();

    this._blurPass(enc, srcTex, tmpTex, width, height, 0 /* H */, radius, sigma);
    this._blurPass(enc, tmpTex, blrTex, width, height, 1 /* V */, radius, sigma);
    this._compositePass(enc, srcTex, blrTex, outTex, width, height, detections, pixelate, pixelSize);

    // WebGPU requires bytesPerRow to be a multiple of 256
    const ALIGN        = 256;
    const rowBytes     = width * 4;
    const alignedRow   = Math.ceil(rowBytes / ALIGN) * ALIGN;
    const stagingSize  = alignedRow * height;

    const staging = d.createBuffer({
      size: stagingSize,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    });
    enc.copyTextureToBuffer(
      { texture: outTex },
      { buffer: staging, bytesPerRow: alignedRow },
      [width, height],
    );

    d.queue.submit([enc.finish()]);

    await staging.mapAsync(GPUMapMode.READ);
    const padded = new Uint8Array(staging.getMappedRange());

    // Strip the row padding to get a tightly-packed RGBA buffer
    const result = new Uint8ClampedArray(rowBytes * height);
    for (let y = 0; y < height; y++) {
      result.set(padded.subarray(y * alignedRow, y * alignedRow + rowBytes), y * rowBytes);
    }

    staging.unmap();

    // Cleanup GPU resources
    srcTex.destroy(); tmpTex.destroy(); blrTex.destroy(); outTex.destroy(); staging.destroy();

    return new ImageData(result, width, height);
  }

  _blurPass(enc, src, dst, width, height, direction, radius, sigma) {
    const d = this.device;

    // struct Params { width, height, direction, radius, sigma } = 5×u32/f32 = 20 bytes → pad to 32
    const buf = d.createBuffer({ size: 32, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
    const view = new DataView(new ArrayBuffer(32));
    view.setUint32(0,  width,     true);
    view.setUint32(4,  height,    true);
    view.setUint32(8,  direction, true);
    view.setUint32(12, radius,    true);
    view.setFloat32(16, sigma,    true);
    d.queue.writeBuffer(buf, 0, view.buffer);

    const bg = d.createBindGroup({
      layout: this._blurPipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: buf } },
        { binding: 1, resource: src.createView() },
        { binding: 2, resource: dst.createView() },
      ],
    });

    const pass = enc.beginComputePass();
    pass.setPipeline(this._blurPipeline);
    pass.setBindGroup(0, bg);
    pass.dispatchWorkgroups(Math.ceil(width / WG), Math.ceil(height / WG));
    pass.end();
  }

  _compositePass(enc, orig, blurred, out, width, height, detections, pixelate, pixelSize) {
    const d = this.device;

    // Params uniform (5 × 4 bytes = 20, pad to 32)
    const paramBuf = d.createBuffer({ size: 32, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
    const pv = new DataView(new ArrayBuffer(32));
    pv.setUint32(0,  width,    true);
    pv.setUint32(4,  height,   true);
    pv.setUint32(8,  Math.min(detections.length, MAX_FACES), true);
    pv.setUint32(12, pixelate ? 1 : 0, true);
    pv.setFloat32(16, pixelSize, true);
    d.queue.writeBuffer(paramBuf, 0, pv.buffer);

    // Faces storage buffer: MAX_FACES × 4 floats
    // Expand each box by BOX_PADDING fraction on every side so the blur
    // covers a generous margin around the detected face region.
    const BOX_PADDING = cfg.blur.boxPadding;
    const faceData = new Float32Array(MAX_FACES * 4);
    detections.slice(0, MAX_FACES).forEach((f, i) => {
      const pw = (f.x2 - f.x1) * BOX_PADDING;
      const ph = (f.y2 - f.y1) * BOX_PADDING;
      faceData[i * 4]     = Math.max(0,      f.x1 - pw);
      faceData[i * 4 + 1] = Math.max(0,      f.y1 - ph);
      faceData[i * 4 + 2] = Math.min(width,  f.x2 + pw);
      faceData[i * 4 + 3] = Math.min(height, f.y2 + ph);
    });
    const faceBuf = d.createBuffer({
      size: faceData.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    d.queue.writeBuffer(faceBuf, 0, faceData);

    const bg = d.createBindGroup({
      layout: this._compositePipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: paramBuf } },
        { binding: 1, resource: { buffer: faceBuf } },
        { binding: 2, resource: orig.createView() },
        { binding: 3, resource: blurred.createView() },
        { binding: 4, resource: out.createView() },
      ],
    });

    const pass = enc.beginComputePass();
    pass.setPipeline(this._compositePipeline);
    pass.setBindGroup(0, bg);
    pass.dispatchWorkgroups(Math.ceil(width / WG), Math.ceil(height / WG));
    pass.end();
  }

  destroy() {
    this.device.destroy();
  }
}
